import React from "react"

import { useMediaMatch } from "rooks"

import s from "./InvestmentsGraph.module.scss"
import { InvestmentRate } from "src/modules/types"
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { numberWithThousandSeparator } from "src/lib/numbers"

type InvestmentsGraphProps = {
    data?: InvestmentRate[]
    currency?: string
    zeroInterestRateLabel?: string
    estimatedRefundLabel?: string
    tooltipTitle?: string
    showLegend?: boolean
}

export const InvestmentsGraph = ({ data, currency, zeroInterestRateLabel, estimatedRefundLabel, tooltipTitle, showLegend = false }: InvestmentsGraphProps) => {

    if (typeof document === "undefined") {
        React.useLayoutEffect = React.useEffect
    }

    const [dimensions, setDimensions] = React.useState({ height: 362, width: 488 })
    const isMobile = useMediaMatch("(max-width: 640px)")

    const renderCustomTickLabel = ({ payload, y }) => {
        const content = isMobile ? `${(payload.value/1000).toFixed(0)}k` : `${numberWithThousandSeparator(payload.value)} ${currency}`
        return <text x={0} y={y} fill="#000000" textAnchor="left">{content}</text>
    }

    const renderTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className={s["custom-tooltip"]}>
                    <p><strong>{tooltipTitle}</strong>: {label}</p>
                    <p><strong>{estimatedRefundLabel}</strong> : {numberWithThousandSeparator(payload[0].value)} {currency}</p>
                    <p><strong>{zeroInterestRateLabel}</strong> : {numberWithThousandSeparator(payload[1].value)} {currency}</p>
                </div>
            )
        }

        return null
    }

    React.useEffect(() => {
        if (isMobile) setDimensions({ height: 288, width: 360 })
        else setDimensions({ height: 288, width: 512 })
    }, [isMobile])

    return (
        <div className={s["graph"]} style={{ width: "100%", height: dimensions.height }}>
            <ResponsiveContainer>
                <LineChart data={ data } margin={{ left: 16, right: 16, bottom: 4 }}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}/>
                    <XAxis
                        tickMargin={16}
                        dataKey="year"
                        interval={2}
                        tickLine={false}
                    />
                    <YAxis
                        tickMargin={16}
                        tick={renderCustomTickLabel}
                        allowDataOverflow={true}
                        tickLine={false}
                        axisLine={false}
                    />
                    <Tooltip content={renderTooltip} />
                    { showLegend && <Legend iconType="circle" iconSize={8} wrapperStyle={{ bottom: "-11px" }} /> }
                    <Line
                        animationDuration={0}
                        type="monotone"
                        dataKey="earning"
                        stroke="#73CCA3"
                        name={estimatedRefundLabel ?? ""}
                        dot={false}
                    />
                    <Line
                        animationDuration={0}
                        type="monotone"
                        name={zeroInterestRateLabel ?? ""}
                        dataKey="flatEarning"
                        stroke="#004D43"
                        dot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}


